import React, { useState, useEffect, useCallback } from 'react';
import { groceryAPI } from '../services/api';
import { useCurrentList } from '../context/CurrentListContext';
import { GroceryItem, SortMethod } from '../types';
import { CATEGORIES } from '../utils/constants';
import GroceryItemModal from '../components/GroceryItemModal';
import MobileMenu from '../components/MobileMenu';
import { Container } from '../components/layout/Container';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import {
  SearchBar,
  CategoryFilters,
  GroceryItemCard,
  SortControls,
  ItemList,
  ColumnHeader,
  LayoutToggle,
} from '../components/shopping';

interface ShoppingListProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

const SORT_LABELS: Record<SortMethod, string> = {
  default: 'Par défaut',
  az: 'A → Z',
  za: 'Z → A',
};
const SORT_CYCLE: SortMethod[] = ['default', 'az', 'za'];

const ShoppingList: React.FC<ShoppingListProps> = ({ activeView, onViewChange }) => {
  const { currentListId } = useCurrentList();
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortMethod, setSortMethod] = useState<SortMethod>('default');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<GroceryItem | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<'toBuy' | 'bought'>('toBuy');
  const [layoutMode, setLayoutMode] = useState<'single' | 'double'>(() => {
    return (localStorage.getItem('shoppingLayoutMode') as 'single' | 'double') || 'single';
  });

  const handleLayoutChange = (mode: 'single' | 'double') => {
    setLayoutMode(mode);
    localStorage.setItem('shoppingLayoutMode', mode);
  };

  const cycleSortMethod = () => {
    const next = SORT_CYCLE[(SORT_CYCLE.indexOf(sortMethod) + 1) % SORT_CYCLE.length];
    setSortMethod(next);
  };

  const fetchItems = useCallback(async () => {
    if (!currentListId) { setItems([]); setLoading(false); return; }
    try {
      setLoading(true);
      const response = await groceryAPI.getAll(currentListId);
      if (response.success && response.data) {
        setItems(response.data);
      } else {
        setError('Failed to load grocery items');
      }
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load grocery items');
    } finally {
      setLoading(false);
    }
  }, [currentListId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleItemUpdate = async (itemId: string, updates: Partial<GroceryItem>) => {
    try {
      setItems((prev) => prev.map((item) => (item._id === itemId ? { ...item, ...updates } : item)));
      const response = await groceryAPI.update(itemId, updates);
      if (!response.success) { await fetchItems(); setError('Failed to update item'); }
    } catch (err) {
      console.error('Error updating item:', err);
      await fetchItems();
      setError('Failed to update item');
    }
  };

  const handleItemDelete = async (itemId: string) => {
    try {
      setItems((prev) => prev.filter((item) => item._id !== itemId));
      const response = await groceryAPI.delete(itemId);
      if (!response.success) { await fetchItems(); setError('Failed to delete item'); }
    } catch (err) {
      console.error('Error deleting item:', err);
      await fetchItems();
      setError('Failed to delete item');
    }
  };

  const handleBulkUpdate = async (action: 'buyAll' | 'addAllToBuy') => {
    try {
      setBulkLoading(true);
      const newBoughtStatus = action === 'buyAll';
      setItems((prev) =>
        prev.map((item) => {
          const matchesCategory = categoryFilter === 'All' || item.type === categoryFilter;
          const matchesSearch = !searchTerm.trim() ||
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.type.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesBoughtStatus = item.bought !== newBoughtStatus;
          return matchesCategory && matchesSearch && matchesBoughtStatus
            ? { ...item, bought: newBoughtStatus } : item;
        })
      );
      if (!currentListId) return;
      const response = await groceryAPI.bulkUpdate(currentListId, action, { bought: !newBoughtStatus });
      if (!response.success) { await fetchItems(); setError('Failed to perform bulk update'); }
    } catch (err) {
      console.error('Error performing bulk update:', err);
      await fetchItems();
      setError('Failed to perform bulk update');
    } finally {
      setBulkLoading(false);
    }
  };

  const sortItems = (itemsToSort: GroceryItem[]): GroceryItem[] => {
    const sorted = [...itemsToSort];
    if (sortMethod === 'az') return sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sortMethod === 'za') return sorted.sort((a, b) => b.name.localeCompare(a.name));
    return sorted.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  };

  const filterItems = (itemsToFilter: GroceryItem[], bought: boolean) => {
    let filtered = itemsToFilter.filter((item) => item.bought === bought);
    if (categoryFilter !== 'All') filtered = filtered.filter((item) => item.type === categoryFilter);
    if (searchTerm.trim()) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return sortItems(filtered);
  };

  const toBuyItems = filterItems(items, false);
  const boughtItems = filterItems(items, true);

  const commonItemProps = {
    onToggleBought: (item: GroceryItem) => { if (item._id) handleItemUpdate(item._id, { bought: !item.bought }); },
    onToggleImportant: (item: GroceryItem) => { if (item._id) handleItemUpdate(item._id, { important: !item.important }); },
    onQuantityChange: (item: GroceryItem, delta: number) => {
      if (item._id) handleItemUpdate(item._id, { quantity: Math.max(1, item.quantity + delta) });
    },
    onEdit: (item: GroceryItem) => { setEditingItem(item); setShowModal(true); },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement de la liste de courses...</div>
      </div>
    );
  }

  return (
    <>
      <MobileMenu
        activeView={activeView}
        onViewChange={onViewChange}
        onAddItem={() => setShowModal(true)}
        onBuyAll={toBuyItems.length > 0 ? () => handleBulkUpdate('buyAll') : undefined}
        onAddAllToBuy={boughtItems.length > 0 ? () => handleBulkUpdate('addAllToBuy') : undefined}
        showShoppingActions={true}
        bulkLoading={bulkLoading}
      />

      <main className="w-full max-w-full min-w-0 max-md:flex-1 max-md:flex max-md:flex-col max-md:overflow-hidden max-md:min-h-0 bg-gray-50 py-4 md:py-8 md:min-h-screen">
        <Container maxWidth="xl" padding={false} className="w-full max-w-full max-md:flex-1 max-md:flex max-md:flex-col max-md:overflow-hidden max-md:min-h-0">

          {/* Header - Desktop Only */}
          <div className="hidden md:flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Liste de courses</h2>
            <Button variant="primary" size="md" onClick={() => setShowModal(true)}>
              + Ajouter un article
            </Button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-danger rounded-lg text-danger text-sm">
              {error}
            </div>
          )}

          {/* Search Bar */}
          <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher..." className="mb-3" />

          {/* Category Filters */}
          <div className="w-full overflow-hidden mb-4">
            <CategoryFilters
              categories={CATEGORIES}
              activeCategory={categoryFilter}
              onCategoryChange={setCategoryFilter}
              scrollable
            />
          </div>

          {/* Mobile Section */}
          <div className="md:hidden flex-1 flex flex-col overflow-hidden min-h-0">

            {/* Single compact row: [Sort] [← pill toggle →] [Layout] */}
            <div className="flex items-center gap-2 mb-4 flex-shrink-0 sm:px-5">
              <button
                type="button"
                onClick={cycleSortMethod}
                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full shadow-soft hover:bg-gray-50 transition-colors duration-200"
              >
                <span>
                  {sortMethod === 'default' && '↕'}
                  {sortMethod === 'az' && '↑'}
                  {sortMethod === 'za' && '↓'}
                </span>
                {SORT_LABELS[sortMethod]}
              </button>

              {/* Segmented pill tab toggle */}
              <div className="relative flex flex-1 bg-gray-100 rounded-full p-0.5">
                <span
                  className={`
                    absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] bg-white rounded-full shadow-sm
                    transition-transform duration-300 ease-in-out
                    ${mobileActiveTab === 'bought' ? 'translate-x-[calc(100%+2px)]' : 'translate-x-0'}
                  `}
                />
                <button
                  type="button"
                  onClick={() => setMobileActiveTab('toBuy')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-full text-xs font-semibold transition-colors duration-200 ${mobileActiveTab === 'toBuy' ? 'text-gray-900' : 'text-gray-500'}`}
                >
                  À acheter
                  <Badge variant="warning" size="sm">{toBuyItems.length}</Badge>
                </button>
                <button
                  type="button"
                  onClick={() => setMobileActiveTab('bought')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-full text-xs font-semibold transition-colors duration-200 ${mobileActiveTab === 'bought' ? 'text-gray-900' : 'text-gray-500'}`}
                >
                  Acheté
                  <Badge variant="info" size="sm">{boughtItems.length}</Badge>
                </button>
              </div>

              <LayoutToggle mode={layoutMode} onChange={handleLayoutChange} />
            </div>

            {/* Single-column layout */}
            {layoutMode === 'single' && (
              <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <ItemList
                  items={mobileActiveTab === 'toBuy' ? toBuyItems : boughtItems}
                  emptyMessage={mobileActiveTab === 'toBuy' ? 'Aucun article à acheter' : 'Aucun article acheté'}
                  {...commonItemProps}
                />
              </div>
            )}

            {/* Two-column compact layout */}
            {layoutMode === 'double' && (
              <div className="flex-1 min-h-0 flex gap-2 w-full overflow-hidden">
                {/* À acheter column */}
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="flex items-center gap-1.5 mb-2 pb-2 border-b-2 border-primary flex-shrink-0">
                    <span className="text-xs font-semibold text-gray-700 truncate">À acheter</span>
                    <span className="flex-shrink-0 inline-flex items-center justify-center text-xs font-bold bg-amber-100 text-amber-700 rounded-full w-5 h-5">
                      {toBuyItems.length}
                    </span>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {toBuyItems.length === 0 ? (
                      <p className="text-xs text-center text-gray-400 py-6">Aucun article</p>
                    ) : (
                      toBuyItems.map((item) => (
                        <GroceryItemCard key={item._id} item={item} compact {...commonItemProps} />
                      ))
                    )}
                  </div>
                </div>

                {/* Acheté column */}
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="flex items-center gap-1.5 mb-2 pb-2 border-b-2 border-success flex-shrink-0">
                    <span className="text-xs font-semibold text-gray-700 truncate">Acheté</span>
                    <span className="flex-shrink-0 inline-flex items-center justify-center text-xs font-bold bg-blue-100 text-blue-700 rounded-full w-5 h-5">
                      {boughtItems.length}
                    </span>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {boughtItems.length === 0 ? (
                      <p className="text-xs text-center text-gray-400 py-6">Aucun article</p>
                    ) : (
                      boughtItems.map((item) => (
                        <GroceryItemCard key={item._id} item={item} compact {...commonItemProps} />
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Two-Column Layout */}
          <div className="hidden md:grid grid-cols-2 gap-6">
            <div className="w-full">
              <ColumnHeader
                title="À acheter"
                count={toBuyItems.length}
                badgeVariant="warning"
                borderColor="border-primary"
                bulkAction={
                  toBuyItems.length > 0
                    ? { label: bulkLoading ? 'Traitement...' : '✓ Tout acheter', onClick: () => handleBulkUpdate('buyAll'), disabled: bulkLoading, variant: 'success' }
                    : undefined
                }
                showSort
                sortControls={<SortControls activeSort={sortMethod} onSortChange={setSortMethod} />}
              />
              <ItemList items={toBuyItems} emptyMessage="Aucun article à acheter" {...commonItemProps} />
            </div>

            <div className="w-full">
              <ColumnHeader
                title="Acheté"
                count={boughtItems.length}
                badgeVariant="info"
                borderColor="border-success"
                bulkAction={
                  boughtItems.length > 0
                    ? { label: bulkLoading ? 'Traitement...' : '← Tout remettre', onClick: () => handleBulkUpdate('addAllToBuy'), disabled: bulkLoading, variant: 'secondary' }
                    : undefined
                }
              />
              <ItemList items={boughtItems} emptyMessage="Aucun article acheté" {...commonItemProps} />
            </div>
          </div>

        </Container>
      </main>

      <GroceryItemModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={fetchItems}
        listId={currentListId}
        itemToEdit={editingItem}
        onDelete={
          editingItem
            ? () => { handleItemDelete(editingItem._id!); setShowModal(false); setEditingItem(null); }
            : undefined
        }
      />
    </>
  );
};

export default ShoppingList;
